import { useState, useRef, ReactNode } from "react";
import { Overlay, OverlayProps, Classes, Button, IconName } from "@blueprintjs/core";
import Draggable, { DraggableEventHandler } from "react-draggable";
import { Resizable, ResizableProps, ResizeCallbackData } from "react-resizable";
import classNames from "classnames";
import {IconNames} from "@blueprintjs/icons";

export interface ModalProps extends Omit<OverlayProps, "onClose"> {
    showHidden?: boolean;
    onHiddenClick?: () => void;
    title?: ReactNode;
    onConfirm?: () => void;
    onClose?: () => void;
    onDrag?: () => void;
    width?: number;
    height?: number;
    zIndex?: number;
    minWidth?: number;
    minHeight?: number;
}

export const Modal = ({
                          showHidden = false,
                          onHiddenClick,
                          title,
                          children,
                          onConfirm,
                          onDrag,
                          width = 500,
                          height = 212,
                          minWidth = 220,
                          minHeight = 64,
                          zIndex = 100,
                          ...props
                      }: ModalProps) => {
    // 拖拽相关变量
    const position = useRef<{ x: number; y: number }>();
    const [disabled, setDisabled] = useState(true);
    const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
    const draggleRef = useRef<HTMLDivElement>(null);

    // 新增：放大相关状态（记录原始尺寸/位置）
    const originalState = useRef<{
        size: { width: number; height: number };
        position: { x: number; y: number } | undefined;
    }>({ size: { width, height }, position: undefined });
    const [isMaximized, setIsMaximized] = useState(false);

    // 拖拽事件
    const onStart: DraggableEventHandler = (_event, uiData) => {
        if (isMaximized) return; // 最大化时禁止拖拽
        const { clientWidth, clientHeight } = window.document.documentElement;
        const targetRect = draggleRef.current?.getBoundingClientRect();
        if (!targetRect) return;
        setBounds({
            left: -targetRect.left + uiData.x,
            right: clientWidth - (targetRect.right - uiData.x),
            top: -targetRect.top + uiData.y,
            bottom: clientHeight - (targetRect.bottom - uiData.y),
        });
    };

    // 尺寸调整事件
    const [size, setSize] = useState<{ width: number; height: number }>({
        width: width > window.innerWidth ? window.innerWidth : width,
        height: height > window.innerHeight ? window.innerHeight - 60 : height,
    });
    const onResise: ResizableProps["onResize"] = (
        _e: React.SyntheticEvent,
        { size }: ResizeCallbackData
    ) => {
        if (isMaximized) return; // 最大化时禁止调整大小
        setSize({ width: size.width, height: size.height });
    };

    // 新增：放大/还原切换
    const toggleMaximize = () => {
        if (!isMaximized) {
            // 放大：记录原始状态，设置全屏尺寸
            originalState.current = {
                size: { ...size },
                position: position.current,
            };
            setSize({ width: window.innerWidth, height: window.innerHeight });
            setDisabled(true);
        } else {
            // 还原：恢复原始状态
            const { size: originalSize, position: originalPos } = originalState.current;
            setSize(originalSize);
            position.current = originalPos;
        }
        setIsMaximized(!isMaximized);
    };

    return (
        <Overlay
            shouldReturnFocusOnClose={false}
            canOutsideClickClose={false}
            canEscapeKeyClose={false}
            enforceFocus={false}
            autoFocus={false}
            hasBackdrop={false}
            {...props}
        >
            <div
                className={Classes.DIALOG_CONTAINER}
                style={{
                    zIndex,
                    ...(isMaximized ? { position: "fixed", top: 0, left: 0 } : {})
                }}
            >
                <Draggable
                    disabled={disabled || isMaximized}
                    bounds={bounds}
                    // onStart={onStart}
                    onDrag={onDrag?.bind(this, undefined)}
                    onStop={(_e, data) => {
                        if (!isMaximized) position.current = { x: data.x, y: data.y };
                    }}
                    defaultPosition={position.current}
                    position={isMaximized ? { x: 0, y: 0 } : undefined}
                >
                    <Resizable
                        width={size.width}
                        height={size.height}
                        maxConstraints={[window.innerWidth, window.innerHeight]}
                        onResize={onResise}
                        minConstraints={[minWidth, minHeight]}
                        // disabled={isMaximized}
                    >
                        <div
                            ref={draggleRef}
                            onClick={(event) => event.stopPropagation()}
                            className={classNames(
                                Classes.DIALOG,
                                "!pb-0",
                                isMaximized && "!rounded-0 !m-0"
                            )}
                            style={{
                                width: size.width,
                                height: size.height,
                                ...(isMaximized ? { position: "fixed", top: 0, left: 0, zIndex } : {})
                            }}
                        >
                            <div
                                className={classNames(
                                    Classes.DIALOG_HEADER,
                                    "cursor-move",
                                    isMaximized && "pointer-events-none"
                                )}
                                onMouseOut={() => !isMaximized && setDisabled(true)}
                                onMouseOver={() => !isMaximized && setDisabled(false)}
                            >
                                <div className={Classes.HEADING}>{title}</div>
                                <div className="flex items-center gap-1">
                                    {showHidden && (
                                        <Button
                                            aria-label="hidden"
                                            minimal
                                            icon={"minus"}
                                            onClick={onHiddenClick}
                                            className="pointer-events-auto"
                                        />
                                    )}
                                    {/* 新增：放大/还原按钮 */}
                                    <Button
                                        aria-label={isMaximized ? "还原" : "放大"}
                                        minimal
                                        icon={isMaximized ? IconNames.MINIMIZE : IconNames.MAXIMIZE} // 替换字符串为枚举值
                                        onClick={toggleMaximize}
                                        className="pointer-events-auto"
                                    />
                                    <Button
                                        aria-label="Close"
                                        /*className={Classes.DIALOG_CLOSE_BUTTON}*/
                                        className="pointer-events-auto"
                                        minimal
                                        icon="cross"
                                        onClick={props.onClose}
                                        // onClick={onClose}
                                    />
                                </div>
                            </div>
                            {/* 内容区域自适应高度 */}
                            {/*<div style={{*/}
                            {/*    height: isMaximized ? `calc(100% - 40px)` : undefined,*/}
                            {/*    overflow: "auto"*/}
                            {/*}}>*/}
                                {children}
                            {/*</div>*/}
                        </div>
                    </Resizable>
                </Draggable>
            </div>
        </Overlay>
    );
};
